import {
  pgTable,
  text,
  serial,
  integer,
  varchar,
  timestamp,
  index,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// ── Admin proposals ─────────────────────────────────────────────────────────
// Prose-driven sales documents authored by the superadmin team. Distinct from
// the broker-side quote workflow (quotes / quote_versions / acceptances).
//
// Each proposal gets a tokenized public URL on first save; the recipient can
// accept by typing their name. Spec 10 update allows acceptance from any
// pre-terminal status (draft / sent / viewed) so offline-signed deals are
// easy to record.
export const adminProposals = pgTable(
  "admin_proposals",
  {
    id: serial("id").primaryKey(),
    opportunityId: integer("opportunity_id"), // FK → opportunities.id; nullable for unattached drafts
    title: text("title").notNull().default("Untitled Proposal"),
    // draft | sent | viewed | accepted | declined | expired
    status: varchar("status", { length: 20 }).notNull().default("draft"),
    intro: text("intro").notNull().default(""),
    services: text("services").notNull().default(""),
    pricing: text("pricing").notNull().default(""),
    timeline: text("timeline").notNull().default(""),
    nextSteps: text("next_steps").notNull().default(""),
    internalNotes: text("internal_notes").notNull().default(""), // never exposed publicly
    publicToken: varchar("public_token", { length: 64 }).notNull().unique(),
    sentAt: timestamp("sent_at"),
    viewedAt: timestamp("viewed_at"),
    acceptedAt: timestamp("accepted_at"),
    signedAt: timestamp("signed_at"),
    signerName: varchar("signer_name", { length: 255 }),
    signerTitle: varchar("signer_title", { length: 255 }),
    signerIp: varchar("signer_ip", { length: 45 }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (t) => ({
    opportunityIdx: index("idx_admin_proposals_opportunity").on(t.opportunityId),
    tokenIdx: index("idx_admin_proposals_token").on(t.publicToken),
    statusIdx: index("idx_admin_proposals_status").on(t.status),
  })
);

export const insertAdminProposalSchema = createInsertSchema(adminProposals)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    sentAt: true,
    viewedAt: true,
    acceptedAt: true,
    signedAt: true,
    signerName: true,
    signerTitle: true,
    signerIp: true,
  })
  .extend({
    title: z.string().min(1).max(500).default("Untitled Proposal"),
  });

export type AdminProposal = typeof adminProposals.$inferSelect;
export type InsertAdminProposal = z.infer<typeof insertAdminProposalSchema>;

// ── MSA template (singleton) ────────────────────────────────────────────────
// One row representing the latest revision of the Master Service Agreement
// template. Authors can paste excerpts from this into proposal sections via
// merge fields (or a dedicated "Insert MSA" affordance — TBD).
export const adminMsaTemplate = pgTable("admin_msa_template", {
  id: serial("id").primaryKey(),
  content: text("content").notNull().default(""),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type AdminMsaTemplate = typeof adminMsaTemplate.$inferSelect;
